import React from 'react';
import { Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';

interface UseAvatarRenderOptions {
  size?: number | 'small' | 'default' | 'large';
  backgroundColor?: string;
  textColor?: string;
  fontSize?: string;
  showFallbackIcon?: boolean;
  useConsistentColors?: boolean;
}

export const useAvatarRender = (options: UseAvatarRenderOptions = {}) => {
  const {
    size = 20,
    backgroundColor = '#1890ff',
    textColor = '#ffffff',
    fontSize = '16px',
    showFallbackIcon = false,
    useConsistentColors = true,
  } = options;

  // Generate consistent color based on name
  const getAvatarColor = (name: string) => {
    const pastelColors = [
      { bg: '#FFD3B6', text: '#5C2E00' }, // soft peach
      { bg: '#B5EAD7', text: '#004D40' }, // mint green
      { bg: '#C7CEEA', text: '#2C2F5C' }, // periwinkle
      { bg: '#FFDAC1', text: '#5E3929' }, // apricot
      { bg: '#E0BBE4', text: '#4A235A' }, // lavender
      { bg: '#D5AAFF', text: '#3B1C66' }, // light violet
      { bg: '#FDCB82', text: '#4D270C' }, // yellow-orange
      { bg: '#A0E7E5', text: '#003636' }, // aqua
      { bg: '#FFABAB', text: '#5E1D1D' }, // rose
      { bg: '#D0F4DE', text: '#0B3D2C' }, // soft green
      { bg: '#FFB6C1', text: '#4A2C2A' }, // light pink
      { bg: '#B0E0E6', text: '#1A3C3C' }, // powder blue
      { bg: '#F0E68C', text: '#4A3C00' }, // khaki
      { bg: '#98FB98', text: '#2F4F2F' }, // pale green
      { bg: '#E6E6FA', text: '#2F2F4F' }, // lavender blush
    ];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    const index = Math.abs(hash) % pastelColors.length;
    return pastelColors[index];
  };

  return React.useCallback((record: any) => {
    // Generate initials from first_name and last_name
    const getInitials = (firstName?: string, lastName?: string) => {
      if (firstName && lastName) {
        return `${firstName[0]}${lastName[0]}`.toUpperCase();
      } else if (firstName) {
        return firstName[0].toUpperCase();
      }
      return 'U';
    };

    const initials = getInitials(record.first_name, record.last_name);
    const fullName = `${record.first_name || ''} ${record.last_name || ''}`.trim();
    
    // Use consistent colors if enabled and name exists
    const avatarColor = useConsistentColors && fullName 
      ? getAvatarColor(fullName)
      : { bg: backgroundColor, text: textColor };

    return (
      <Avatar
        size={size}
        style={{
          backgroundColor: avatarColor.bg,
          color: avatarColor.text,
          fontWeight: 600,
          fontSize,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        icon={showFallbackIcon && initials === 'U' ? <UserOutlined /> : undefined}
      >
        {record.avatar ? (
          <img
            src={record.avatar}
            alt={fullName || 'User Avatar'}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          initials
        )}
      </Avatar>
    );
  }, [size, backgroundColor, textColor, fontSize, showFallbackIcon, useConsistentColors]);
};
